import React from 'react';
import { PropTypes } from 'prop-types';
import './ImageUploadSection.scss';
import { Form, Button, Input, Upload, Space, InputNumber } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { Divider } from 'rc-menu';
import Text from 'antd/lib/typography/Text';
import { uploadImage } from '../../services/imageService';
import _ from 'lodash';

const ImageUploadSection = ({
  beforeImageUpload,
  nest,
  path,
  bucket,
  setStatus,
  form,
  imgRequired = {},
  hideResolution = {},
  hideAlt = false,
  // Only need to pass in if in a form list
  listName,
  isDesktopInput = false,
  showWebP = false
}) => {
  const [isDesktopAdded, setIsDesktopAdded] = React.useState(
    imgRequired.desktop
  );
  const [loading, setLoading] = React.useState({
    mobilePng: false,
    mobileWebP: false,
    desktopPng: false,
    desktopWebP: false
  });
  const onImageUpload = async (e, type, field) => {
    // store image in correct folder based on type
    let updatedPath =
      e.file.type === 'image/jpeg'
        ? `${path}jpeg/`
        : e.file.type === 'image/png'
        ? `${path}png/`
        : e.file.type === 'image/webp'
        ? `${path}webp/`
        : path;
    const formData = new FormData();
    formData.append('name', e.file.name);
    formData.append('bucket', bucket);
    formData.append('path', updatedPath);
    formData.append('image', e.file);
    await uploadImage(formData)
      .then((res) => {
        form.setFieldsValue(setFields(type, res.data.imageUrl, field));
        setLoading({
          mobilePng: false,
          mobileWebP: false,
          desktopPng: false,
          desktopWebP: false
        });
      })
      .catch((err) => {
        alert(err);
        setLoading(false);
        setStatus({ type: 'ERROR', message: 'Something went wrong!' });
      });
  };

  const onMobileImageUpload = async (e) => {
    onImageUpload(e, 'mobileImgData', 'src');
  };

  const onDesktopImageUpload = async (e) => {
    setIsDesktopAdded(true);
    onImageUpload(e, 'desktopImgData', 'src');
  };

  const onMobileWebpImageUpload = async (e) => {
    onImageUpload(e, 'mobileImgData', 'webpSrc');
  };

  const onDesktopWebpImageUpload = async (e) => {
    onImageUpload(e, 'desktopImgData', 'webpSrc');
  };

  // Need to make sure we populate the values in the right place of the form. Nested values gets passed as
  // an array so need to created nested object from that and then add src field at correct level
  const setFields = (type, url, field) => {
    let newNestedObject = {};
    // check and prepend list name if it is a list, otherwise use nested structure as is
    const nestArr = listName ? [listName, ...nest] : nest;
    nestArr.reduce(function (o, s, i) {
      return (o[s] =
        i === nestArr.length - 1 ? { [type]: { [field]: url } } : {});
    }, newNestedObject);

    // Retain old values of object, overwrite with new values
    newNestedObject = _.merge(form.getFieldsValue(), newNestedObject);
    return newNestedObject;
  };

  return (
    <section>
      {!hideAlt ?? (
        <Form.Item
          label={'alt'}
          name={[...nest, 'alt']}
          rules={[{ required: false }]}
          fieldKey={[...nest, 'alt']}>
          <Input />
        </Form.Item>
      )}
      <Text style={{ fontWeight: 600 }}>
        Mobile img{' '}
        {imgRequired.mobile && <span className="required-sign">*</span>}
      </Text>
      <Upload
        beforeUpload={beforeImageUpload}
        customRequest={onMobileImageUpload}
        showUploadList={false}
        onChange={() =>
          setLoading((e) => {
            return { ...e, mobilePng: true };
          })
        }
        style={{ display: 'flex', justifyContent: 'end' }}>
        <Button
          icon={<UploadOutlined />}
          style={{ marginBottom: '8px' }}
          loading={loading.mobilePng}>
          Click to Upload <strong> PNG/JPG </strong> Mobile Image
        </Button>
      </Upload>
      <Form.Item
        label={'Src'}
        name={[...nest, 'mobileImgData', 'src']}
        rules={[{ required: imgRequired.mobile }]}
        fieldKey={[...nest, 'mobileImgData', 'src']}>
        <Input />
      </Form.Item>
      {showWebP && (
        <>
          <Upload
            beforeUpload={beforeImageUpload}
            customRequest={onMobileWebpImageUpload}
            showUploadList={false}
            onChange={() =>
              setLoading((e) => {
                return { ...e, mobileWebP: true };
              })
            }>
            <Button
              icon={<UploadOutlined />}
              style={{ marginBottom: '8px' }}
              loading={loading.mobileWebP}>
              Click to Upload <strong> WebP </strong> Mobile Image
            </Button>
          </Upload>
          <Form.Item
            label={'webpSrc'}
            name={[...nest, 'mobileImgData', 'webpSrc']}
            rules={[{ required: imgRequired.mobile }]}
            fieldKey={[...nest, 'mobileImgData', 'webpSrc']}>
            <Input />
          </Form.Item>
        </>
      )}

      {!hideResolution.mobile ? (
        <>
          <Form.Item
            label={'Width'}
            name={[...nest, 'mobileImgData', 'meta', 'width']}
            rules={[{ required: false }]}
            fieldKey={[...nest, 'mobileImgData', 'meta', 'width']}>
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            label={'Height'}
            name={[...nest, 'mobileImgData', 'meta', 'height']}
            rules={[{ required: false }]}
            fieldKey={[...nest, 'mobileImgData', 'meta', 'height']}>
            <InputNumber min={0} />
          </Form.Item>
          <Divider />
        </>
      ) : null}

      {(isDesktopAdded || isDesktopInput) && (
        <>
          <Text style={{ fontWeight: 600 }}>
            Desktop img <span className="required-sign"></span>
          </Text>
          <Upload
            beforeUpload={beforeImageUpload}
            showUploadList={false}
            customRequest={onDesktopImageUpload}
            onChange={() =>
              setLoading((e) => {
                return { ...e, desktopPng: true };
              })
            }>
            <Button
              icon={<UploadOutlined />}
              style={{ marginBottom: '8px' }}
              loading={loading.desktopPng}>
              Click to Upload <strong> PNG/JPG </strong> Desktop Image
            </Button>
          </Upload>
          <Form.Item
            label={'Src'}
            name={[...nest, 'desktopImgData', 'src']}
            rules={[{ required: imgRequired.desktop }]}
            fieldKey={[...nest, 'desktopImgData', 'src']}>
            <Input />
          </Form.Item>
          {showWebP && (
            <>
              <Upload
                beforeUpload={beforeImageUpload}
                customRequest={onDesktopWebpImageUpload}
                showUploadList={false}
                onChange={() =>
                  setLoading((e) => {
                    return { ...e, desktopWebP: true };
                  })
                }>
                <Button
                  icon={<UploadOutlined />}
                  style={{ marginBottom: '8px' }}
                  loading={loading.desktopWebP}>
                  Click to Upload <strong> WebP </strong> Desktop Image
                </Button>
              </Upload>
              <Form.Item
                label={'webpSrc'}
                name={[...nest, 'desktopImgData', 'webpSrc']}
                rules={[{ required: imgRequired.desktop }]}
                fieldKey={[...nest, 'desktopImgData', 'webpSrc']}>
                <Input />
              </Form.Item>
            </>
          )}
          {!hideResolution.desktop && (
            <>
              <Form.Item
                label={'Width'}
                name={[...nest, 'desktopImgData', 'meta', 'width']}
                rules={[{ required: false }]}
                fieldKey={[...nest, 'desktopImgData', 'meta', 'width']}>
                <InputNumber min={0} />
              </Form.Item>
              <Form.Item
                label={'Height'}
                name={[...nest, 'desktopImgData', 'meta', 'height']}
                rules={[{ required: false }]}
                fieldKey={[...nest, 'desktopImgData', 'meta', 'height']}>
                <InputNumber min={0} />
              </Form.Item>
            </>
          )}
        </>
      )}
    </section>
  );
};

export default ImageUploadSection;
